import { Arrow, Button } from '@sprd/sprd-component-kit/v2';
import { FC } from 'react';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import Link from 'next/link';

type PaginationItem = {
  href?: string;
  onClick?: () => void;
};

type PaginationProps = {
  className?: string;
  // It uses 0-based index
  activePageIndex: number;
  pages: PaginationItem[];
  // Accessibility label to make nav items distinguishable
  label: string;
  // Custom handlers for prev/next navigation (used when pages don't have href)
  onPrevClick?: () => void;
  onNextClick?: () => void;
};

const Pagination: FC<PaginationProps> = ({
  className,
  activePageIndex,
  pages,
  label,
  onPrevClick,
  onNextClick,
}) => {
  const t = useTranslations('pagination');

  const hasHiddenPages = pages.length > 4;
  const hasLeftEllipsis = hasHiddenPages && activePageIndex > 2;
  const hasRightEllipsis = hasHiddenPages && activePageIndex < pages.length - 2;

  const pagesWithIndex = pages.map((page, index) => ({ ...page, pageIndex: index }));
  const pagesToShow = [];

  if (!hasHiddenPages) {
    for (let page = 1; page < pages.length - 1; page++) {
      pagesToShow.push(pagesWithIndex[page]);
    }
  } else if (!hasLeftEllipsis) {
    pagesToShow.push(pagesWithIndex[1], pagesWithIndex[2]);
  } else if (!hasRightEllipsis) {
    pagesToShow.push(pagesWithIndex[pages.length - 3], pagesWithIndex[pages.length - 2]);
  } else {
    pagesToShow.push(
      pagesWithIndex[activePageIndex - 1],
      pagesWithIndex[activePageIndex],
      pagesWithIndex[activePageIndex + 1]
    );
  }

  const prevPage = pages[activePageIndex - 1];
  const nextPage = pages[activePageIndex + 1];

  return (
    <nav className={clsx('flex items-center justify-center', className)} aria-label={label}>
      {/* Previous Button */}
      {prevPage?.href ? (
        <Button
          className="!gap-0 !border-0 !px-2"
          asChild
          startIcon={
            <Arrow className="size-4 rotate-180 md:size-6" aria-hidden={true} fill="#FFFFFF" />
          }
          variant="primary"
        >
          <Link prefetch={false} href={prevPage.href} aria-label={t('a11y.previousPage')} />
        </Button>
      ) : (
        <Button
          className="!gap-0 !border-0 !px-2"
          startIcon={
            <Arrow
              className="size-4 rotate-180"
              aria-hidden={true}
              fill={!!prevPage ? '#FFFFFF' : '#AFAFAF'}
            />
          }
          variant={!prevPage ? 'ghost-invert' : 'primary'}
          disabled={!prevPage}
          aria-label={t('a11y.previousPage')}
          onClick={onPrevClick}
        />
      )}

      <div className="inline-flex items-center justify-center gap-1 px-4">
        {!!pages[0] && (
          <PaginationItem page={pages[0]} isActive={activePageIndex === 0} pageNumber={1} />
        )}
        {hasLeftEllipsis && <EllipsisItem />}
        {pagesToShow.map((page) => {
          if (!page) {
            return null;
          }

          return (
            <PaginationItem
              key={page.pageIndex}
              page={page}
              isActive={activePageIndex === page.pageIndex}
              pageNumber={page.pageIndex + 1}
            />
          );
        })}
        {hasRightEllipsis && <EllipsisItem />}
        {pages.length !== 1 && (
          <PaginationItem
            page={pages[pages.length - 1]!}
            isActive={activePageIndex === pages.length - 1}
            pageNumber={pages.length}
          />
        )}
      </div>

      {/* Next Button */}
      {nextPage?.href ? (
        <Button
          className={clsx('!gap-0 !border-0 !px-2', 'md:!gap-2 md:!px-6')}
          asChild
          endIcon={<Arrow className="size-4 md:size-6" aria-hidden={true} fill="#FFFFFF" />}
          variant="primary"
        >
          <Link prefetch={false} href={nextPage.href} aria-label={t('nextPage')}>
            <span
              className="hidden items-center justify-center text-sm leading-tight font-semibold md:flex"
              aria-hidden={true}
            >
              {t('nextPage')}
            </span>
          </Link>
        </Button>
      ) : (
        <Button
          className={clsx('!border-0 !px-2')}
          endIcon={
            <Arrow
              className="size-4"
              aria-hidden={true}
              fill={!!nextPage ? '#FFFFFF' : '#AFAFAF'}
            />
          }
          variant={!nextPage ? 'ghost-invert' : 'primary'}
          disabled={!nextPage}
          aria-label={t('nextPage')}
          onClick={onNextClick}
        />
      )}
    </nav>
  );
};

// Unified Pagination Item Component
type PaginationItemProps = {
  page: PaginationItem;
  pageNumber: number;
  isActive: boolean;
};

const PaginationItem: FC<PaginationItemProps> = ({ pageNumber, page, isActive }) => {
  const t = useTranslations('pagination');

  // If href exists, render as link
  if (page?.href) {
    return (
      <Button
        className={clsx(
          '!size-8 !px-2 !text-xs md:!size-10 md:!text-sm',
          isActive && '!bg-neutral-100'
        )}
        asChild
        variant="plain"
      >
        <Link
          prefetch={false}
          href={page.href}
          aria-label={t('a11y.goToPage', { page: pageNumber })}
          aria-current={isActive ? 'page' : undefined}
        >
          {pageNumber}
        </Link>
      </Button>
    );
  }

  // Otherwise render as button
  return (
    <Button
      className={clsx(
        '!size-8 !px-2 !text-xs md:!size-10 md:!text-sm',
        isActive && '!bg-neutral-100'
      )}
      variant="plain"
      aria-label={t('a11y.goToPage', { page: pageNumber })}
      aria-current={isActive ? 'page' : undefined}
      onClick={page?.onClick}
    >
      {pageNumber}
    </Button>
  );
};

const EllipsisItem: FC = () => (
  <span
    className="flex h-8 w-4 items-center justify-center text-xs leading-tight font-semibold md:size-10 md:text-sm"
    aria-hidden={true}
  >
    ...
  </span>
);

export default Pagination;
